// src/components/principal/StaffManager.jsx
import React, { useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import api from "../../services/api";

const StaffManager = ({ onError }) => {
  const [staff, setStaff] = useState([]);
  const [textEmails, setTextEmails] = useState("");
  const [file, setFile] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmEmails, setConfirmEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const fileRef = useRef(null);

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim().replace(/,+$/, ""));

  const extractEmailsFromFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          if (file.name.endsWith(".csv")) {
            Papa.parse(file, {
              complete: (results) => {
                const emails = results.data
                  .flat()
                  .map((e) => String(e).trim().replace(/,+$/, ""))
                  .filter(validateEmail);
                resolve(
                  [...new Set(emails.map((e) => e.toLowerCase()))].map((e) =>
                    emails.find((g) => g.toLowerCase() === e)
                  )
                );
              },
              error: (err) => reject(err),
            });
          } else if (file.name.endsWith(".xlsx")) {
            const wb = XLSX.read(data, { type: "array" });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const emails = json
              .flat()
              .map((e) => String(e).trim().replace(/,+$/, ""))
              .filter(validateEmail);
            resolve(
              [...new Set(emails.map((e) => e.toLowerCase()))].map((e) =>
                emails.find((g) => g.toLowerCase() === e)
              )
            );
          } else {
            reject(new Error("Unsupported file format. Please upload a CSV or XLSX file."));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file."));
      if (file.name.endsWith(".xlsx")) reader.readAsArrayBuffer(file);
      else reader.readAsText(file);
    });

  const loadStaff = async () => {
    try {
      const { data } = await api.get("/auth/staff-list");
      setStaff(data || []);
    } catch {
      onError?.("Failed to load staff list.");
    }
  };

  useEffect(() => {
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChooseFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.match(/\.(csv|xlsx)$/)) {
      onError?.("Unsupported file format. Please upload a CSV or XLSX file.");
      return;
    }
    setFile(f);
  };

  const startCreate = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setLoading(true);
    try {
      let valid = [];
      if (file) {
        valid = await extractEmailsFromFile(file);
      } else {
        const raw = textEmails
          .split(/[\s,;]+/) // split on whitespace, comma, or semicolon
          .map((e) => e.trim().replace(/,+$/, ""));
        const deduped = [...new Set(raw.map((e) => e.toLowerCase()))].map((e) =>
          raw.find((g) => g.toLowerCase() === e)
        );
        valid = deduped.filter(validateEmail);
        const invalid = raw.filter((e) => e && !validateEmail(e));
        if (invalid.length) return onError?.(`Invalid email(s): ${invalid.join(", ")}`);
      }
      if (!valid.length) return onError?.("Please enter at least one valid email or upload a file.");
      setConfirmEmails(valid);
      setShowConfirm(true);
    } finally {
      setLoading(false);
    }
  };

  const confirmCreate = async () => {
    setLoading(true);
    setShowConfirm(false);
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        res = await api.post("/auth/create-staff/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post(
          "/auth/create-staff",
          confirmEmails.map((email) => ({ email }))
        );
      }
      const msg = res.data.message;
      const failed = res.data.failed;
      if (failed?.length) {
        onError?.(`${msg} Failed: ${failed.join(", ")}`);
      } else if (msg) {
        setSuccessMessage(msg);
      } else {
        setSuccessMessage("Staff accounts processed successfully.");
      }
      await loadStaff();
      setFile(null);
      setTextEmails("");
      if (fileRef.current) fileRef.current.value = null;
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to create staff accounts.");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (id, email) => {
    if (!window.confirm(`Are you sure you want to deactivate ${email}?`)) return;
    try {
      await api.delete(`/auth/revoke-access/${email}`);
      setStaff((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      onError?.(e.response?.data?.detail || `Failed to de-activate ${email}.`);
    }
  };

  return (
    <div className="staff-content">
      <h3>Current Staff</h3>

      {!staff.length ? (
        <p className="no-staff-message">No Staff Accounts created yet</p>
      ) : (
        <div className="staff-table-wrap" role="region" aria-label="Current staff list">
          <table className="staff-table" aria-describedby="staff-table-caption">
            <caption id="staff-table-caption" className="sr-only">
              Current staff members
            </caption>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => {
                const status = String(s.status || "UNKNOWN").toUpperCase();
                const isActive = status === "ACTIVE";
                const displayName = s.display_name || s.email; // fallback
                const isDefaultName =
                  !s.display_name ||
                  displayName.trim().toLowerCase() === s.email.trim().toLowerCase();

                return (
                  <tr key={s.id || s.email} className="staff-row">
                    <td className={`staff-name-td ${isDefaultName ? "is-default-name" : ""}`}>
                      <span className="staff-name-text">{displayName}</span>
                    </td>
                    <td className="staff-email-td">
                      <span className="staff-email-text">{s.email}</span>
                    </td>
                    <td>
                      <span className={`status-pill ${isActive ? "is-active" : "is-inactive"}`}>
                        {status}
                      </span>
                    </td>
                    <td className="staff-actions-td">
                      <button
                        className="btn-danger compact"
                        onClick={() => revoke(s.id, s.email)}
                        aria-label={`Revoke access for ${s.email}`}
                        title="Revoke Access"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h3>Create New Staff</h3>
      <form onSubmit={startCreate}>
        <textarea
          value={textEmails}
          onChange={(e) => setTextEmails(e.target.value)}
          placeholder="Type or paste staff emails (comma/space separated) or upload a CSV/XLSX below."
          rows={6}
        />
        <button
          type="submit"
          disabled={loading}
          style={{ backgroundColor: "orange", color: "white", padding: "10px 20px", marginBottom: 20 }}
        >
          Create Staff
        </button>

        <p>
          Or choose a CSV/Excel file with emails and then click the orange &quot;Create Staff&quot; button.
        </p>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onChooseFile} />
      </form>

      {successMessage && (
        <p className="success-message" aria-live="polite">
          {successMessage}
        </p>
      )}

      {showConfirm && (
        <div
          className="confirmation-dialog"
          style={{ marginTop: 10, padding: 10, border: "1px solid #ccc", background: "#106ebe" }}
        >
          <p>Are you sure? Accounts will be created for:</p>
          <ul>{confirmEmails.map((e, i) => <li key={i}>{e}</li>)}</ul>
          <button onClick={confirmCreate} style={{ marginRight: 10 }}>Proceed</button>
          <button onClick={() => setShowConfirm(false)}>Cancel</button>
        </div>
      )}

      <div className="template-downloads" style={{ marginBottom: 10 }}>
        <ul>
          <li>
            <a
              href="https://ghrianghraffy.com/static/templates/staff_emails_template.csv"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="template-download-link"
            >
              Download CSV Template (Optional)
            </a>
          </li>
          <li>
            <a
              href="https://ghrianghraffy.com/static/templates/staff_emails_template.xlsx"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="template-download-link"
            >
              Download Excel Template (Optional)
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default StaffManager;


